import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import {
  TextField,
  FormControl,
  FormHelperText,
  useMediaQuery,
  Box,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { IDatePickerProps } from "./DatePickerProps";
import moment from "moment";
import "moment/locale/th";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers-pro";
import { I18NextNs, Language, TypeComponent } from "enum/enum";
import { FnComponents, I18n } from "utilities/utilities";
import {
  CustomAdapter,
  dayOfWeekFormatterTH,
  localeMap,
  maskMapFormat,
  modeFormatDate,
  modeOpenView,
} from ".";
import secureLocalStorage from "react-secure-storage";

export const DatePickerItem = (props: IDatePickerProps) => {
  const {
    defaultValue = null,
    disabled = false,
    fullWidth = false,
    id,
    IsMonthPicker = false,
    IsMonthYearPicker = false,
    IsShowIcon = true,
    IsShowMessageError = true,
    IsYearPicker = false,
    label,
    maxDate = null,
    minDate = null,
    name,
    onDisableDay,
    required = false,
    size = "small",
    isBtnAction = false,
    isCancelOnly = false,
    isDisableYear = false,
    nYearMapper = null,
    defaultCalendarMonth = null,
    IsDisplayLabel = false,
    IsDisablePortal = false,
  } = props;
  const lang = secureLocalStorage.getItem(I18n.envI18next);

  const { control, setValue } = useFormContext();

  const rules = useMemo(() => {
    let message = `${I18n.SetText("required", I18NextNs.validation)}`;
    return {
      required: {
        value: required,
        message: message + ` ${label}`,
      },
    };
  }, [label, required]);

  const IsMobile = useMediaQuery("(max-width:768px)");
  const [open, setOpen] = useState(false);
  const locale = useMemo(() => {
    return lang === Language.th ? "th" : "en";
  }, [lang]);

  const formatDefault = modeFormatDate(
    IsMonthYearPicker,
    IsMonthPicker,
    IsYearPicker,
    props.format
  );

  const maskMap = maskMapFormat(formatDefault);

  const objCheckReplace = useMemo(() => {
    localeMap(locale);
    let objReturn = { isReplaceYear: false, indexArr: 0, splitText: "" };
    let formatLower = formatDefault ? formatDefault.toLowerCase() : "";
    if (formatLower.includes("YYYY")) {
      let splitText = formatDefault
        .substring(formatLower.indexOf("YYYY") - 1, 1)
        .toLowerCase();
      if (splitText !== "Y") {
        objReturn.splitText = splitText;
        objReturn.indexArr = formatLower
          .split(splitText)
          .findIndex((e) => e === "YYYY");
      }
      objReturn.isReplaceYear = true;
    }
    return objReturn;
  }, [formatDefault, locale]);

  const handleAddYear = (year) => {
    let nYear = parseInt(year) > 2500 ? parseInt(year) : parseInt(year) + 543;
    return nYear;
  };

  const renderSetFormatDate = (params, value) => {
    if (locale === "th" && value) {
      if (formatDefault === "MMMM YYYY") {
        let convertDateToString: string = moment(value)
          .locale("th")
          .format(formatDefault);
        let year = convertDateToString.split(" ")[1];
        let addyear = handleAddYear(year);
        params.inputProps.value = convertDateToString.replaceAll(
          `${year}`,
          `${addyear}`
        );
      } else if (formatDefault === "YYYY") {
        let year: string = moment(value).format(formatDefault);
        let addyear = handleAddYear(year);
        params.inputProps.value = addyear;
      } else if (formatDefault === "MMMM") {
        let dMoment = moment(value);
        let sFormat = formatDefault;
        if (nYearMapper && isDisableYear) {
          dMoment = dMoment.year(handleAddYear(nYearMapper));
          sFormat = "MMMM YYYY";
        }
        let convertDateToString: string = dMoment.locale("th").format(sFormat);
        params.inputProps.value = convertDateToString;
      } else {
        let year = objCheckReplace.splitText
          ? params.inputProps.value.split(objCheckReplace.splitText)[
          objCheckReplace.indexArr
          ]
          : params.inputProps.value.substr(6, 4);
        let addyear = year > 2500 ? parseInt(year) : parseInt(year) + 543;
        params.inputProps.value = params.inputProps.value.replaceAll(
          `${year}`,
          `${addyear}`
        );
      }
    }
  };

  const renderPaperProps = useCallback(() => {
    let oRender = {};
    if (IsMonthPicker) {
      oRender = {
        sx: {
          ".MuiPickersCalendarHeader-root": {
            display: "none !important",
          },
        },
      };
    }
    return oRender;
  }, [IsMonthPicker]);

  const handleOnchange = (event, onChangeController) => {
    let IsValid = moment(event).isValid();
    let dDate = null;
    if (IsValid) {
      dDate = event;
    }
    onChangeController(dDate);
    setValue(name, dDate, { shouldDirty: true, shouldValidate: true });

    if (props.onChange) {
      props.onChange(dDate);
    }
  };

  const onKeyDown = (e) => {
    e.preventDefault();
  };

  return (
    <Controller
      key={FnComponents.GetKey(TypeComponent.datePicker, name)}
      name={name}
      control={control}
      rules={rules}
      shouldUnregister={true}
      defaultValue={defaultValue}
      render={({
        field: { onChange, value, ref },
        fieldState: { invalid },
      }) => {
        let isValid = invalid && !disabled;
        let helperText = IsShowMessageError
          ? `${I18n.SetText("required", I18NextNs.validation)} ${label}`
          : "";
        return (
          <>
            <LocalizationProvider
              adapterLocale={locale}
              dateAdapter={CustomAdapter as any}
              localeText={{
                clearButtonLabel: I18n.SetText(
                  "DatePicker.Clear",
                  I18NextNs.labelComponent
                ),
                okButtonLabel: I18n.SetText(
                  "DatePicker.Ok",
                  I18NextNs.labelComponent
                ),
                cancelButtonLabel: I18n.SetText(
                  "DatePicker.Cancel",
                  I18NextNs.labelComponent
                ),
              }}
            >
              <FormControl fullWidth={fullWidth}>
                <DatePicker
                  dayOfWeekFormatter={dayOfWeekFormatterTH}
                  inputRef={ref}
                  value={value ? moment(value, formatDefault) : null}
                  PopperProps={{
                    // style: { zIndex: 2000}, 
                    style: { zIndex: 2000, position: IsDisablePortal ? "relative" : "absolute", },
                    // disablePortal: true,
                    disablePortal: IsDisablePortal,
                  }}
                  onChange={(event) => {
                    handleOnchange(event, onChange);
                  }}
                  shouldDisableDate={onDisableDay}
                  open={open}
                  onOpen={() => {
                    if (!props.disabled) {
                      setOpen(true);
                    }
                  }}
                  onClose={() => setOpen(false)}
                  minDate={minDate}
                  maxDate={maxDate}
                  label={IsDisplayLabel === true ? "" : label}
                  defaultCalendarMonth={defaultCalendarMonth}
                  inputFormat={formatDefault}
                  disabled={disabled}
                  views={modeOpenView(
                    IsMonthYearPicker,
                    IsMonthPicker,
                    IsYearPicker,
                    props.view
                  )}
                  showToolbar={false}
                  showDaysOutsideCurrentMonth={false}
                  PaperProps={renderPaperProps()}
                  componentsProps={{
                    actionBar: {
                      actions: isCancelOnly ? ["cancel"] : ["clear", "cancel"],

                      // actions: IsMobile
                      //   ? ["clear", "cancel"]
                      //   : ["clear", "cancel", "accept"],
                      // : (isBtnAction ? ["clear", "cancel", "accept"] : []),
                    },
                  }}
                  renderInput={(params) => {
                    params.inputProps.placeholder = maskMap[locale];
                    (!IsMonthPicker || (isDisableYear && nYearMapper)) && renderSetFormatDate(params, value);
                    return (
                      <TextField
                        inputProps={{ readOnly: true }}
                        {...params}
                        sx={{
                          ".MuiOutlinedInput-root": {
                            padding: IsShowIcon
                              ? "0px 15px 0px 0px!important"
                              : "0px !important",
                            ".MuiInputAdornment-root": {
                              overflow: IsShowIcon ? "" : "auto",
                            },
                            // " fieldset > legend > span": {
                            //   padding: "0px !important",
                            // },
                          },
                          // ".MuiInputLabel-outlined": {
                          //   top: "0px",
                          // },                        
                          fontWeight: 600,
                        }}
                        size={size}
                        label={label}
                        id={FnComponents.GetId(
                          TypeComponent.datePicker,
                          id,
                          name
                        )}
                        name={name}
                        error={invalid}
                        required={required}
                        disabled={disabled}
                        focused={false}
                        value={params.inputProps.value || ""}
                        autoComplete={"off"}
                        onClick={() => {
                          if (!props.disabled) {
                            setOpen(true);
                          }
                        }}
                        onKeyDown={onKeyDown}
                      />
                    );
                  }}
                />
              </FormControl>
            </LocalizationProvider>
            {IsShowMessageError && isValid && (
              <FormHelperText>{helperText}</FormHelperText>
            )}
          </>
        );
      }}
    />
  );
};
